import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 5000;

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
  } catch { return ""; }
};

/**
 * BookingMessagesPanel — collapsible chat tied to a booking.
 * Polls `/api/bookings/{id}/messages` every 5s while open and marks-as-read on each fetch.
 * Used by both VendorBookingsList and CustomerDashboard.
 */
export default function BookingMessagesPanel({ bookingId, counterpartyName, onUnreadChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = async ({ markRead = true } = {}) => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/messages`);
      setMessages(data);
      // Only POST mark-read when there's something unread *for me* on this thread.
      const hasUnreadForMe = markRead && data.some(
        (m) => m.sender_id !== user?.id && !(m.read_by || []).includes(user?.id)
      );
      if (hasUnreadForMe) {
        try { await api.post(`/bookings/${bookingId}/messages/read`); }
        catch (err) { console.error("mark-read failed:", err); }
        onUnreadChange?.();
      }
    } catch (e) {
      console.error("messages load failed:", e);
    }
  };

  // Open / close lifecycle: load + start/stop polling.
  useEffect(() => {
    if (!open) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    setLoading(true);
    fetchMessages().finally(() => setLoading(false));
    pollRef.current = setInterval(() => fetchMessages({ markRead: true }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingId]);

  // Auto-scroll on new message
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/messages`, { body: text });
      setMessages((prev) => [...prev, data]);
      setBody("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const isAdminViewer = user?.role === "admin";
  // Admin acts as mediator — they're not "the other party", they're observing both sides.
  const headerLabel = isAdminViewer
    ? "Mediate booking conversation"
    : `Chat with ${counterpartyName || "the other party"}`;
  const ctaLabel = isAdminViewer
    ? "Open booking chat as mediator →"
    : `Open chat with ${counterpartyName || "the other party"} →`;

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`messages-panel-${bookingId}`}>
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-sm font-medium text-blue-600 hover:underline"
          data-testid={`open-messages-${bookingId}`}
        >
          {ctaLabel}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">
              {headerLabel}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-neutral-500 hover:text-neutral-900"
              data-testid={`close-messages-${bookingId}`}
            >
              Close
            </button>
          </div>

          <div
            ref={scrollRef}
            className="rounded-2xl border border-border bg-neutral-50/40 px-4 py-3 max-h-72 overflow-y-auto space-y-3"
            data-testid={`messages-list-${bookingId}`}
          >
            {loading && messages.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-neutral-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
            ) : messages.length === 0 ? (
              <div className="text-xs text-neutral-500 py-6 text-center">No messages yet — say hi!</div>
            ) : (
              messages.map((m) => {
                const mine = m.sender_id === user?.id;
                // Highlight admin messages with a small pill so customers and vendors
                // can tell instantly when RentalCar.al is stepping in to mediate, vs. talking
                // to each other.
                const isAdminMsg = m.sender_role === "admin";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${mine ? "bg-blue-600 text-white" : isAdminMsg ? "bg-amber-50 border border-amber-200" : "bg-white border border-border"}`}
                      data-testid={`message-${m.id}`}
                    >
                      {!mine && (
                        <div className={`text-[10px] uppercase tracking-wider font-semibold mb-0.5 inline-flex items-center gap-1 ${isAdminMsg ? "text-amber-800" : "opacity-60"}`}>
                          {m.sender_name}{isAdminMsg ? <span className="px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[9px]">RentalCar.al support</span> : null}
                        </div>
                      )}
                      <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                      <div className={`text-[10px] mt-1 ${mine ? "text-blue-100" : "text-neutral-400"}`}>{formatTime(m.created_at)}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-end gap-2">
            <Textarea
              rows={2}
              className="rounded-xl flex-1 resize-none"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Type a message…"
              data-testid={`message-input-${bookingId}`}
              disabled={sending}
            />
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              className="rounded-full bg-blue-600 hover:bg-blue-700 h-10 px-4"
              data-testid={`send-message-${bookingId}`}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
